import type {
  EmailTemplateKey,
  EmailTemplateResponse,
  SiteResponse,
} from '@lms/api-client/backend';
import { HexToHSL } from '@lms/ui/branding';
import { Button } from '@lms/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@lms/ui/resizable';
import { Textarea } from '@lms/ui/textarea';
import { useToast } from '@lms/ui/use-toast';
import { cn } from '@lms/ui/utils';
import { ChevronLeft, TrashIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { useTranslations } from 'use-intl';
import {
  type CodeError,
  removeEmailTemplate,
  renderMjml,
  saveTemplate,
} from '@/features/email/email-api';
import { EMAIL_TEMPLATES_KEY } from '@/features/email/email-keys';

const ExampleVariables: Record<EmailTemplateKey, Record<string, string>> = {
  magiclink: {
    user_name: 'Anders Svensson',
    login_link: 'https://courses.safeassetgroup.com/safe/login',
  },
  invite: {
    curriculum: 'Anställd',
    code: 'ABC123',
    login_link: 'https://courses.safeassetgroup.com/safe/login',
  },
};

// Ports app/[brand]/admin/email/_components/mjml-preview.tsx. The Next component
// wrote the rendered HTML imperatively onto an iframe ref and used
// next/link + server actions; here the render result is held in state and fed to
// `<iframe srcDoc={html}>`, `next/link` becomes react-router `Link`, and the
// delete action navigates back to the list with `useNavigate` (in place of
// `revalidatePath`, the list SWR key is invalidated).
const MjmlPreview: React.FC<{
  site: SiteResponse;
  template: EmailTemplateResponse;
}> = ({ site, template }) => {
  const [mjmlCode, setMjmlCode] = useState<string>(template.template);
  const [subject, setSubject] = useState<string>(template.subject);
  const [subjectPreview, setSubjectPreview] = useState<string>(
    template.subject,
  );
  const [html, setHtml] = useState<string>('');
  const [errors, setErrors] = useState<CodeError[]>([]);
  const t = useTranslations();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const [templateVariables] = useState<Record<string, string>>({
    site_logo: site.logo,
    site_name: site.name,
    site_color: site.primaryColor,
    site_color_text: HexToHSL(site.primaryColor).l > 50 ? '#000000' : '#ffffff',
    ...ExampleVariables[template.key],
  });

  useEffect(() => {
    (async () => {
      const result = await renderMjml(
        { ...template, template: mjmlCode, subject },
        templateVariables,
      );
      setErrors(result.errors);
      setSubjectPreview(result.subject);
      setHtml(result.html);
    })();
  }, [mjmlCode, templateVariables, subject, template]);

  const saveChanges = useCallback(
    async (mjml: string, nextSubject: string) => {
      try {
        await saveTemplate(template.id, mjml, nextSubject);
        toast({ description: t('admin.saved_changes') });
      } catch (e) {
        toast({
          title: t('global.error'),
          description: `${e}`,
          variant: 'destructive',
        });
      }
    },
    [template, t, toast],
  );

  const remove = useCallback(async () => {
    const result = await removeEmailTemplate(template.id);
    if (result.ok) {
      await mutate(EMAIL_TEMPLATES_KEY);
      navigate(`/${site.slug}/admin/email`);
    } else {
      toast({
        title: t('global.error'),
        description: result.error,
        variant: 'destructive',
      });
    }
  }, [template, site.slug, mutate, navigate, toast, t]);

  const changed =
    mjmlCode !== template.template || subject !== template.subject;

  return (
    <>
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" className="h-7 w-7" asChild>
          <Link to={`/${site.slug}/admin/email`}>
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">{t('admin.go_back')}</span>
          </Link>
        </Button>
        <h1 className="flex-1 shrink-0 text-xl font-semibold tracking-tight whitespace-nowrap sm:grow-0">
          {t(`email.Templates.${template.key}.name`)}
        </h1>
        <div className="hidden items-center gap-2 md:ml-auto md:flex">
          <Button
            variant="destructive"
            onClick={remove}
            disabled={template.siteId === null}
          >
            <TrashIcon size={20} className="mr-2" />
            {t('admin.remove', { what: t('admin.template').toLowerCase() })}
          </Button>
          <Button
            variant="outline"
            onClick={() => setMjmlCode(template.template)}
            disabled={!changed}
          >
            {t('admin.discard')}
          </Button>
          <Button
            disabled={!changed}
            onClick={() => saveChanges(mjmlCode, subject)}
          >
            {t('admin.save')}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.info')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg">
              {t(`email.Templates.${template.key}.name`)}
              {' - '}
              {t(`languages.${template.language}`)}
            </p>
            <p>{t(`email.Templates.${template.key}.description`)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.site')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{template.siteName ?? 'Global'}</p>
          </CardContent>
        </Card>
      </div>

      <div>
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.preview')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResizablePanelGroup
              direction="horizontal"
              className="min-h-[800px] w-full rounded-lg border"
            >
              <ResizablePanel defaultSize={50} className="flex flex-col">
                <h1 className="m-2 border-b-2 pb-2">
                  <span className="font-semibold">
                    {t('email.subject_line')}
                    {':'}
                  </span>{' '}
                  {subjectPreview}
                </h1>
                <iframe
                  title={t('admin.preview')}
                  srcDoc={html}
                  className="flex-grow"
                />
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel
                defaultSize={50}
                className="flex flex-col gap-4 p-4"
              >
                <div>
                  <Label htmlFor="subject">{t('email.subject_line')}</Label>
                  <Input
                    id="subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
                <Label htmlFor="mjml">{t('admin.template')}</Label>
                <Textarea
                  id="mjml"
                  className={cn(
                    'flex-grow',
                    errors.length > 0 && 'border-destructive ring-destructive',
                  )}
                  value={mjmlCode}
                  onChange={(e) => setMjmlCode(e.target.value)}
                />
                <div className="">
                  <ul>
                    {errors.map(({ line, message }) => (
                      <li
                        key={`${line}-${message}`}
                        className="text-destructive"
                      >
                        {t('email.line_error', { line, message })}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="">
                  <h2>{t('email.variables')}</h2>
                  <ul>
                    {Object.entries(templateVariables).map(([key, value]) => (
                      <li key={key}>{`[[${key.toUpperCase()}]] = ${value}`}</li>
                    ))}
                  </ul>
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

MjmlPreview.displayName = 'MjmlPreview';

export { MjmlPreview };
